/**
 * FASTA parsing for the sequence tools.
 *
 * Accepts a bare sequence (no header), one record, or many. Lines beginning with ">"
 * start a record; lines beginning with ";" are old-style comments and are skipped.
 * Whitespace and digits (position numbers in a GenBank-style listing) are removed from
 * sequence lines; every other character is kept for the caller to validate.
 */

export interface SequenceRecord {
  /** The full header line without ">". Empty for a bare sequence. */
  header: string;
  /** The first word of the header, used as an identifier. */
  name: string;
  /** Residues (upper-case unless parsed with `uppercase: false`), whitespace and digits removed. */
  sequence: string;
}

const LINE_BREAK = /\r\n|\r|\n/;
const WHITESPACE = /\s+/g;
const DIGITS = /\d+/g;
const FIRST_WORD = /^\S+/;

export interface ParseFastaOptions {
  /** Upper-case the residues (the default). Pass false to keep the case as typed. */
  uppercase?: boolean;
}

function recordFrom(
  header: string,
  parts: string[],
  uppercase: boolean
): SequenceRecord {
  const joined = parts.join("");
  return {
    header,
    name: header.match(FIRST_WORD)?.[0] ?? "",
    sequence: uppercase ? joined.toUpperCase() : joined,
  };
}

export function parseFasta(
  text: string,
  options: ParseFastaOptions = {}
): SequenceRecord[] {
  const uppercase = options.uppercase !== false;
  const records: SequenceRecord[] = [];
  let header: string | null = null;
  let parts: string[] = [];
  const flush = () => {
    if (header !== null || parts.length > 0) {
      records.push(recordFrom(header ?? "", parts, uppercase));
    }
    header = null;
    parts = [];
  };
  for (const line of text.split(LINE_BREAK)) {
    if (line.startsWith(">")) {
      flush();
      header = line.slice(1).trim();
    } else if (!line.startsWith(";")) {
      const residues = line.replace(WHITESPACE, "").replace(DIGITS, "");
      if (residues !== "") {
        parts.push(residues);
      }
    }
  }
  flush();
  return records.filter(
    (record) => record.sequence !== "" || record.header !== ""
  );
}

/** Wrap a sequence at `width` characters per line (FASTA convention is 60 or 70). */
export function wrapSequence(sequence: string, width = 60): string {
  if (width <= 0 || sequence.length <= width) {
    return sequence;
  }
  const lines: string[] = [];
  for (let i = 0; i < sequence.length; i += width) {
    lines.push(sequence.slice(i, i + width));
  }
  return lines.join("\n");
}

/** One record as FASTA text. */
export function toFasta(header: string, sequence: string, width = 60): string {
  return `>${header}\n${wrapSequence(sequence, width)}`;
}
